/**
 * Resolve the initial `pcpNpi` / `ipaId` seed for PatientPriorityIndex from
 * the Desktop roster (`PanelProvider[]`) plus any per-device persisted picks.
 *
 * Preference:
 * 1. Persisted picks that still exist in the roster (and have an NPI)
 * 2. Gate-resolved (npi, ipa) if that row is in the roster
 * 3. First roster row that carries an NPI
 */
import type { PanelProvider } from "@astrana/ui/modules";

import type { StoredProviderPick } from "./providerSelectionStorage";

export function resolveRosterSeed(args: {
  providers: PanelProvider[];
  stored: StoredProviderPick[];
  gateNpi?: string | null;
  gateIpa?: string | null;
}): { pcpNpi: string; ipaId: string; seedProviders: PanelProvider[] } {
  const { providers, stored, gateNpi, gateIpa } = args;
  if (providers.length === 0) {
    return { pcpNpi: "", ipaId: "", seedProviders: [] };
  }

  const byKey = new Map(
    providers.map((p) => [`${p.provKeyId}|${p.ipa}`, p] as const),
  );

  const restored = stored
    .map((s) => byKey.get(`${s.provKeyId}|${s.ipa}`))
    .filter((p): p is PanelProvider => !!p && !!p.npi?.trim());
  if (restored.length > 0) {
    return {
      pcpNpi: restored[0].npi,
      ipaId: restored[0].ipa,
      seedProviders: restored,
    };
  }

  const npi = gateNpi?.trim();
  const ipa = gateIpa?.trim();
  if (npi) {
    const matched = providers.filter(
      (p) => p.npi === npi && (!ipa || p.ipa === ipa) && !!p.npi.trim(),
    );
    if (matched.length > 0) {
      return { pcpNpi: matched[0].npi, ipaId: matched[0].ipa, seedProviders: matched };
    }
  }

  const firstWithNpi = providers.find((p) => !!p.npi?.trim());
  if (firstWithNpi) {
    return {
      pcpNpi: firstWithNpi.npi,
      ipaId: firstWithNpi.ipa,
      seedProviders: [firstWithNpi],
    };
  }

  return { pcpNpi: "", ipaId: providers[0].ipa, seedProviders: [] };
}

export function picksFromProviders(providers: PanelProvider[]): StoredProviderPick[] {
  return providers.map((p) => ({
    provKeyId: p.provKeyId,
    ipa: p.ipa,
    ...(p.npi?.trim() ? { npi: p.npi.trim() } : {}),
  }));
}
